import { useState } from 'react'
import type { OutputApproval, OutputApprovalStatus } from '@mindkeep/shared'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { StatusBadge } from '@/components/status-badge'
import {
  useDecideOutputApproval,
  useRequestOutputReview,
} from './use-output-approvals'

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info'

const STATUS_META: Record<OutputApprovalStatus, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Entwurf', variant: 'default' },
  pending_review: { label: 'In Prüfung', variant: 'warning' },
  approved: { label: 'Freigegeben', variant: 'success' },
  rejected: { label: 'Abgelehnt', variant: 'danger' },
}

export function OutputApprovalBadge({ status }: { status: OutputApprovalStatus }) {
  const meta = STATUS_META[status]
  return <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function reviewerLabel(reviewerUserId: string | null, memberEmails: Map<string, string>): string {
  if (!reviewerUserId) return 'unbekannt'
  return memberEmails.get(reviewerUserId) ?? `${reviewerUserId.slice(0, 8)}…`
}

/** Reviewer, Datum und Kommentar bei entschiedenen Outputs (approved/rejected). */
export function OutputApprovalDecisionInfo({
  approval,
  memberEmails,
}: {
  approval: OutputApproval
  memberEmails: Map<string, string>
}) {
  if (approval.status !== 'approved' && approval.status !== 'rejected') return null
  const decisionLabel = approval.status === 'approved' ? 'Freigegeben' : 'Abgelehnt'
  return (
    <p className="text-xs text-muted-foreground">
      {decisionLabel} von {reviewerLabel(approval.reviewer_user_id, memberEmails)} am{' '}
      {formatDate(approval.updated_at)}
      {approval.comment ? ` — „${approval.comment}“` : ''}
    </p>
  )
}

interface OutputApprovalActionsProps {
  path: string
  approval?: OutputApproval
  canRequestReview: boolean
  canReview: boolean
}

/**
 * Approval-Aktionen pro Output: member+ meldet zur Review an (solange kein
 * pending/approved-Stand existiert), owner/admin entscheidet bei pending_review.
 * Ablehnen braucht zwingend einen Kommentar (Inline-Eingabe).
 */
export function OutputApprovalActions({
  path,
  approval,
  canRequestReview,
  canReview,
}: OutputApprovalActionsProps) {
  const requestReview = useRequestOutputReview()
  const decideApproval = useDecideOutputApproval()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [comment, setComment] = useState('')

  const showRequest =
    canRequestReview &&
    (!approval || approval.status === 'draft' || approval.status === 'rejected')
  const showDecide = canReview && approval?.status === 'pending_review'
  const isPending = requestReview.isPending || decideApproval.isPending

  if (!showRequest && !showDecide) return null

  function handleRejectConfirm() {
    const trimmed = comment.trim()
    if (!trimmed) return
    decideApproval.mutate(
      { path, decision: 'rejected', comment: trimmed },
      {
        onSuccess: () => {
          setRejectOpen(false)
          setComment('')
        },
      },
    )
  }

  return (
    // F06b2: ruhige Aktionszeile — 44px Ziele mobil, 36px ab 761px (Button-Default).
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
      {showRequest && (
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={() => requestReview.mutate({ path, output_type: outputTypeFromPath(path) })}
        >
          Zur Review anmelden
        </Button>
      )}
      {showDecide && !rejectOpen && (
        <>
          <Button
            size="sm"
            disabled={isPending}
            onClick={() => decideApproval.mutate({ path, decision: 'approved' })}
          >
            Freigeben
          </Button>
          <Button variant="outline" size="sm" disabled={isPending} onClick={() => setRejectOpen(true)}>
            Ablehnen
          </Button>
        </>
      )}
      {showDecide && rejectOpen && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Kommentar (Pflicht)"
            aria-label="Kommentar zur Ablehnung"
            className="h-11 w-48 max-w-full min-[761px]:h-9"
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleRejectConfirm()
            }}
          />
          <Button
            size="sm"
            variant="destructive"
            disabled={isPending || comment.trim().length === 0}
            onClick={handleRejectConfirm}
          >
            Ablehnen bestätigen
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isPending}
            onClick={() => {
              setRejectOpen(false)
              setComment('')
            }}
          >
            Abbrechen
          </Button>
        </div>
      )}
    </div>
  )
}

/** output_type aus dem Vault-Pfad: _Outputs/<task>/<datei> -> <task>. */
function outputTypeFromPath(path: string): string {
  const segments = path.split('/')
  const dir = segments.length >= 3 && segments[0] === '_Outputs' ? segments[1] : undefined
  return dir ?? 'output'
}
